import { Fragment, useMemo, useState } from "react";
import { createFileRoute, redirect, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { getMyAdminStatus } from "@/lib/billing.functions";
import { listAdminLeads } from "@/lib/admin-leads.functions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SECTORS,
  SECTOR_LABELS,
  STAGES,
  STAGE_LABELS,
  GEOGRAPHIES,
  GEOGRAPHY_LABELS,
  RAISE_TYPES,
  RAISE_TYPE_LABELS,
  formatDealSize,
} from "@/lib/classification-vocab";

// Accept either an array (TanStack's JSON-encoded round-trip) or a
// comma-separated string (legacy/manual URLs). Always normalize to string[].
const strArray = fallback(
  z
    .union([z.array(z.string()), z.string()])
    .optional()
    .transform((v) => {
      if (!v) return [] as string[];
      if (Array.isArray(v))
        return v.map((s) => String(s).trim()).filter(Boolean);
      return v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }),
  [] as string[],
).default([] as string[]);

const searchSchema = z.object({
  days: fallback(z.number().int().min(1).max(365), 30).default(30),
  page: fallback(z.number().int().min(1).max(10000), 1).default(1),
  sector: strArray,
  stage: strArray,
  geography: strArray,
  raise_type: strArray,
  deal_size_min: fallback(z.number().int().min(0).optional(), undefined),
  deal_size_max: fallback(z.number().int().min(0).optional(), undefined),
});

export const Route = createFileRoute("/_authed/admin/leads")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Admin: Lead-gen users: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminLeadsPage,
});

const PAGE_SIZE = 25;

function fmtUsd(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86400_000);
  if (d <= 0) return "today";
  if (d === 1) return "1d ago";
  if (d < 30) return `${d}d ago`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function toMillionsDisplay(rawDollars: number | null | undefined): string {
  if (rawDollars == null || !Number.isFinite(Number(rawDollars))) return "";
  const v = Number(rawDollars) / 1_000_000;
  const s = v.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

function parseMillions(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 1_000_000);
}

type MultiOption = { value: string; label: string };

function MultiSelectFilter({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: MultiOption[];
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(values);
  const summary =
    values.length === 0
      ? label
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${label} · ${values.length}`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 justify-between gap-2"
        >
          <span className="truncate text-[12.5px]">{summary}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-2" align="start">
        <div className="mb-1 flex items-center justify-between px-1 text-[11px] uppercase tracking-wide text-brand-muted">
          <span>{label}</span>
          {values.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-brand-ink underline"
              onClick={() => onChange([])}
            >
              clear
            </button>
          )}
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {options.map((opt) => {
            const checked = selectedSet.has(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-muted/60"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => {
                    if (c) onChange([...values, opt.value]);
                    else onChange(values.filter((v) => v !== opt.value));
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function toOptions<T extends string>(
  arr: readonly T[],
  labels: Record<T, string>,
): MultiOption[] {
  return arr.map((v) => ({ value: v, label: labels[v] }));
}

function AdminLeadsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/leads" });
  const { days, page } = search;

  const sectors: string[] = (search.sector ?? []) as string[];
  const stages: string[] = (search.stage ?? []) as string[];
  const geographies: string[] = (search.geography ?? []) as string[];
  const raiseTypes: string[] = (search.raise_type ?? []) as string[];
  const sizeMin = search.deal_size_min;
  const sizeMax = search.deal_size_max;

  const filtersActive =
    sectors.length > 0 ||
    stages.length > 0 ||
    geographies.length > 0 ||
    raiseTypes.length > 0 ||
    sizeMin != null ||
    sizeMax != null;

  const setSearch = (
    patch: Partial<Record<keyof typeof search, unknown>>,
    resetPage = true,
  ) => {
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...patch,
        ...(resetPage ? { page: 1 } : {}),
      }) as never,
    });
  };

  const [minRaw, setMinRaw] = useState<string>(
    sizeMin != null ? toMillionsDisplay(sizeMin) : "",
  );
  const [maxRaw, setMaxRaw] = useState<string>(
    sizeMax != null ? toMillionsDisplay(sizeMax) : "",
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchList = useServerFn(listAdminLeads);
  const q = useQuery({
    queryKey: [
      "admin-leads",
      days,
      page,
      sectors.join(","),
      stages.join(","),
      geographies.join(","),
      raiseTypes.join(","),
      sizeMin ?? "",
      sizeMax ?? "",
    ],
    queryFn: () =>
      fetchList({
        data: {
          days,
          page,
          page_size: PAGE_SIZE,
          sectors: sectors.length ? sectors : undefined,
          stages: stages.length ? stages : undefined,
          geographies: geographies.length ? geographies : undefined,
          raise_types: raiseTypes.length ? raiseTypes : undefined,
          deal_size_min: sizeMin,
          deal_size_max: sizeMax,
        },
      }),
    refetchOnWindowFocus: false,
  });

  const totalPages = q.data
    ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE))
    : 1;

  const sectorOptions = useMemo(
    () => toOptions(SECTORS, SECTOR_LABELS),
    [],
  );
  const stageOptions = useMemo(() => toOptions(STAGES, STAGE_LABELS), []);
  const geoOptions = useMemo(
    () => toOptions(GEOGRAPHIES, GEOGRAPHY_LABELS),
    [],
  );
  const raiseOptions = useMemo(
    () => toOptions(RAISE_TYPES, RAISE_TYPE_LABELS),
    [],
  );

  const commitMin = () => {
    const v = parseMillions(minRaw);
    setSearch({ deal_size_min: v ?? undefined });
  };
  const commitMax = () => {
    const v = parseMillions(maxRaw);
    setSearch({ deal_size_max: v ?? undefined });
  };

  const clearAll = () => {
    setMinRaw("");
    setMaxRaw("");
    setExpanded(new Set());
    navigate({ search: () => ({ days, page: 1 }) });
  };

  const totalUsers = q.data?.total ?? 0;

  return (
    <main className="mx-auto max-w-[1280px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Lead-gen users.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Users active within the recency window, sorted by credits purchased
          (descending). Top 50 capped. Click a row to open the user
          drilldown.
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-brand-muted">Active within</span>
        <Select
          value={String(days)}
          onValueChange={(v) => setSearch({ days: Number(v) })}
        >
          <SelectTrigger className="h-9 w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
          </SelectContent>
        </Select>
        <MultiSelectFilter
          label="Sector"
          options={sectorOptions}
          values={sectors}
          onChange={(v) => setSearch({ sector: v })}
        />
        <MultiSelectFilter
          label="Stage"
          options={stageOptions}
          values={stages}
          onChange={(v) => setSearch({ stage: v })}
        />
        <MultiSelectFilter
          label="Geography"
          options={geoOptions}
          values={geographies}
          onChange={(v) => setSearch({ geography: v })}
        />
        <MultiSelectFilter
          label="Raise type"
          options={raiseOptions}
          values={raiseTypes}
          onChange={(v) => setSearch({ raise_type: v })}
        />
        <div className="flex items-center gap-1">
          <Input
            value={minRaw}
            onChange={(e) => setMinRaw(e.target.value)}
            onBlur={commitMin}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            placeholder="Min $M"
            className="h-9 w-[90px] px-2.5 text-[13px]"
          />
          <span className="text-brand-muted">–</span>
          <Input
            value={maxRaw}
            onChange={(e) => setMaxRaw(e.target.value)}
            onBlur={commitMax}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            placeholder="Max $M"
            className="h-9 w-[90px] px-2.5 text-[13px]"
          />
        </div>
        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 gap-1 text-[12.5px]"
            onClick={clearAll}
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </Button>
        )}
      </div>

      {filtersActive && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[12px]">
          {sectors.map((s) => (
            <Badge key={`s-${s}`} variant="secondary" className="gap-1">
              {SECTOR_LABELS[s as keyof typeof SECTOR_LABELS] ?? s}
              <button
                type="button"
                onClick={() =>
                  setSearch({ sector: sectors.filter((x) => x !== s) })
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {stages.map((s) => (
            <Badge key={`st-${s}`} variant="secondary" className="gap-1">
              {STAGE_LABELS[s as keyof typeof STAGE_LABELS] ?? s}
              <button
                type="button"
                onClick={() =>
                  setSearch({ stage: stages.filter((x) => x !== s) })
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {geographies.map((s) => (
            <Badge key={`g-${s}`} variant="secondary" className="gap-1">
              {GEOGRAPHY_LABELS[s as keyof typeof GEOGRAPHY_LABELS] ?? s}
              <button
                type="button"
                onClick={() =>
                  setSearch({ geography: geographies.filter((x) => x !== s) })
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {raiseTypes.map((s) => (
            <Badge key={`r-${s}`} variant="secondary" className="gap-1">
              {RAISE_TYPE_LABELS[s as keyof typeof RAISE_TYPE_LABELS] ?? s}
              <button
                type="button"
                onClick={() =>
                  setSearch({ raise_type: raiseTypes.filter((x) => x !== s) })
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {sizeMin != null && (
            <Badge variant="secondary" className="gap-1">
              ≥ {formatDealSize(sizeMin)}
              <button
                type="button"
                onClick={() => {
                  setMinRaw("");
                  setSearch({ deal_size_min: undefined });
                }}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {sizeMax != null && (
            <Badge variant="secondary" className="gap-1">
              ≤ {formatDealSize(sizeMax)}
              <button
                type="button"
                onClick={() => {
                  setMaxRaw("");
                  setSearch({ deal_size_max: undefined });
                }}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      <p className="mb-3 text-[12.5px] text-brand-muted">
        {filtersActive
          ? `Showing ${totalUsers.toLocaleString()} matching users (top 50 capped).`
          : `${totalUsers.toLocaleString()} total users in window.`}
      </p>

      <section className="rounded-2xl border border-brand-line bg-brand-paper p-2">
        {q.isLoading && (
          <div className="grid gap-2 p-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {q.error && (
          <p className="p-4 text-[14px] text-destructive">
            {q.error instanceof Error ? q.error.message : "Failed to load"}
          </p>
        )}
        {q.data && q.data.rows.length === 0 && (
          <p className="m-3 rounded-lg border border-dashed border-brand-line bg-background/50 px-4 py-6 text-center text-[14px] text-brand-muted">
            No leads matched this window.
          </p>
        )}
        {q.data && q.data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[28px]" />
                  <TableHead>User</TableHead>
                  <TableHead className="w-[120px] text-right">
                    Credits bought
                  </TableHead>
                  <TableHead className="w-[80px] text-right">Deals</TableHead>
                  {filtersActive && (
                    <TableHead className="w-[110px] text-right">
                      Matching
                    </TableHead>
                  )}
                  <TableHead className="w-[80px] text-right">Runs</TableHead>
                  <TableHead className="w-[120px] text-right">
                    Cost to serve
                  </TableHead>
                  <TableHead className="w-[120px]">Last active</TableHead>
                  <TableHead className="w-[110px]">Account age</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.rows.map((r) => {
                  const isOpen = expanded.has(r.user_id);
                  const canExpand =
                    filtersActive && r.matching_deals.length > 0;
                  const colSpan = filtersActive ? 8 : 7;
                  return (
                    <Fragment key={r.user_id}>
                      <TableRow className="hover:bg-muted/40">
                        <TableCell className="p-2">
                          {canExpand && (
                            <button
                              type="button"
                              className="rounded p-0.5 text-brand-muted hover:bg-muted hover:text-brand-ink"
                              onClick={() => {
                                const next = new Set(expanded);
                                if (isOpen) next.delete(r.user_id);
                                else next.add(r.user_id);
                                setExpanded(next);
                              }}
                              aria-label={isOpen ? "Collapse" : "Expand"}
                            >
                              {isOpen ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-[13px]">
                          <a
                            href={`/admin/users/${r.user_id}`}
                            className="text-brand-ink underline"
                          >
                            {r.email ?? r.user_id.slice(0, 8) + "…"}
                          </a>
                          <div className="font-mono text-[11px] text-brand-muted">
                            {r.user_id.slice(0, 8)}…
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[13px] text-brand-ink">
                          {r.credits_purchased.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[13px]">
                          {r.deals_count}
                        </TableCell>
                        {filtersActive && (
                          <TableCell className="text-right tabular-nums text-[13px] text-brand-ink">
                            {r.matching_deals_count ?? 0}
                          </TableCell>
                        )}
                        <TableCell className="text-right tabular-nums text-[13px]">
                          {r.runs_completed}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[13px] text-brand-muted">
                          {fmtUsd(r.total_cost_usd)}
                        </TableCell>
                        <TableCell className="text-[12.5px] text-brand-muted">
                          {relTime(r.last_active)}
                        </TableCell>
                        <TableCell className="text-[12.5px] text-brand-muted">
                          {r.account_age_days}d
                        </TableCell>
                      </TableRow>
                      {isOpen && canExpand && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell />
                          <TableCell colSpan={colSpan - 1} className="py-2">
                            <div className="overflow-x-auto rounded-lg border border-brand-line bg-background">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Deal</TableHead>
                                    <TableHead className="w-[140px]">
                                      Sector
                                    </TableHead>
                                    <TableHead className="w-[100px] text-right">
                                      Size
                                    </TableHead>
                                    <TableHead className="w-[140px]">
                                      Stage
                                    </TableHead>
                                    <TableHead className="w-[110px]">
                                      Created
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {r.matching_deals.map((d) => (
                                    <TableRow key={d.id}>
                                      <TableCell className="text-[13px]">
                                        <Link
                                          to="/deals/$dealId"
                                          params={{ dealId: d.id }}
                                          className="text-brand-ink underline"
                                        >
                                          {d.name ?? d.id.slice(0, 8) + "…"}
                                        </Link>
                                        <div className="font-mono text-[11px] text-brand-muted">
                                          {d.id.slice(0, 8)}…
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-[12.5px]">
                                        {d.sector
                                          ? (SECTOR_LABELS[
                                              d.sector as keyof typeof SECTOR_LABELS
                                            ] ?? d.sector)
                                          : "—"}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums text-[12.5px]">
                                        {formatDealSize(d.deal_size_usd) ?? "—"}
                                      </TableCell>
                                      <TableCell className="text-[12.5px]">
                                        {d.stage
                                          ? (STAGE_LABELS[
                                              d.stage as keyof typeof STAGE_LABELS
                                            ] ?? d.stage)
                                          : "—"}
                                      </TableCell>
                                      <TableCell className="text-[12px] text-brand-muted">
                                        {new Date(
                                          d.created_at,
                                        ).toLocaleDateString()}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-[12.5px] text-brand-muted">
          Page {page} of {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || q.isFetching}
            onClick={() => setSearch({ page: Math.max(1, page - 1) }, false)}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages || q.isFetching}
            onClick={() => setSearch({ page: page + 1 }, false)}
          >
            Next
          </Button>
        </div>
      </div>
    </main>
  );
}
